import { useQuery } from '@tanstack/react-query';
import { useSyncExternalStore } from 'react';
import { api } from './api';

export type SalonLocation = {
  id: string;
  name: string;
  slug: string;
  phone: string | null;
  address: { street: string | null; houseNumber: string | null; postcode: string | null; city: string | null };
  timeZone: string;
  bookingEnabled: boolean;
};

/** Which location this device is looking at, per salon (a per-viewer convenience). */
const listeners = new Set<() => void>();
const keyFor = (orgId: string) => `location:${orgId}`;
function readChoice(orgId: string): string | null {
  try {
    return localStorage.getItem(keyFor(orgId));
  } catch {
    return null;
  }
}
export function chooseLocation(orgId: string, id: string) {
  try {
    localStorage.setItem(keyFor(orgId), id);
  } catch {
    // storage unavailable: the choice lasts until reload
  }
  memory.set(orgId, id);
  listeners.forEach((l) => l());
}
const memory = new Map<string, string>();

export function useLocations(orgId: string) {
  return useQuery({ queryKey: ['locations', orgId], queryFn: () => api<{ locations: SalonLocation[] }>(`/orgs/${orgId}/locations`), select: (d) => d.locations });
}

/** The location the app shows (calendar, Vandaag, opening hours). Falls back to the first one. */
export function useCurrentLocation(orgId: string) {
  const chosen = useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => memory.get(orgId) ?? readChoice(orgId),
  );
  return useQuery({
    queryKey: ['locations', orgId],
    queryFn: () => api<{ locations: SalonLocation[] }>(`/orgs/${orgId}/locations`),
    select: (d) => d.locations.find((l) => l.id === chosen) ?? d.locations[0],
  });
}
